// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createAssetOperation } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DatePicker from 'shared_items/DatePicker';

// ###### HELPERS
const todayYYYYMMDD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const getInitialState = () => ({
  asset: '',
  operation_status: 'buy',
  operation_date: todayYYYYMMDD(),
  redeem_date: '',
  quantity: '',
  total_value: '',
  net_value: '',
  rate_description: '',
  notes: '',
});

// ###### COMPONENT
const ModalOperationAdd = ({ isOpen, onClose, onSuccess, assets = [] }) => {
  const [form, setForm] = useState(getInitialState);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialState());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'operation_status' && value === 'sell' ? { redeem_date: '' } : {}),
    }));
  };

  const handleStatusClick = (value) => {
    setForm((prev) => ({
      ...prev,
      operation_status: value,
      ...(value === 'sell' ? { redeem_date: '' } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const assetId = form.asset?.trim() ? Number(form.asset) : null;
    if (!assetId) {
      toast.error('Selecione um asset.');
      return;
    }
    if (!form.operation_date?.trim()) {
      toast.error('Data da operação é obrigatória.');
      return;
    }
    const totalVal = parseDecimal(form.total_value);
    if (totalVal === null) {
      toast.error('Valor total é obrigatório e deve ser um número válido.');
      return;
    }
    const netVal = parseDecimal(form.net_value);
    if (form.net_value?.trim() !== '' && netVal === null) {
      toast.error('Valor líquido inválido.');
      return;
    }
    const qty = parseDecimal(form.quantity);
    if (form.quantity?.trim() !== '' && qty === null) {
      toast.error('Quantidade inválida.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        asset: assetId,
        operation_status: form.operation_status,
        operation_date: form.operation_date,
        redeem_date: form.operation_status === 'buy' && form.redeem_date?.trim() ? form.redeem_date : null,
        quantity: qty,
        total_value: totalVal,
        net_value: netVal != null ? netVal : totalVal,
        rate_description: form.rate_description?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      await createAssetOperation(payload);
      toast.success('Operação criada.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao criar operação.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">Nova operação</h2>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Asset</label>
            <select
              name="asset"
              value={form.asset}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white"
              disabled={submitting}
              required
            >
              <option value="">— Selecione —</option>
              {(assets || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.asset_code ?? a.id} — {a.asset_name ?? '—'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Tipo</label>
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
              {[
                { value: 'buy', label: 'Buy' },
                { value: 'sell', label: 'Sell' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  disabled={submitting}
                  onClick={() => handleStatusClick(value)}
                  className={`px-3 py-1 text-xs font-medium transition-all ${
                    value === 'buy' ? 'rounded-l-full' : 'rounded-r-full'
                  } ${
                    form.operation_status === value
                      ? value === 'buy'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-rose-100 text-rose-800'
                      : 'text-gray-700 hover:bg-gray-200'
                  } ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <div className="w-1/2 min-w-0 shrink-0">
              <label className="block text-xs text-gray-500 mb-0.5">Data da operação</label>
              <DatePicker
                name="operation_date"
                value={form.operation_date}
                onChange={handleInputChange}
                mode="single"
                colorScheme="green"
                disabled={submitting}
                required
                placeholder="Select date"
                className="w-full justify-between min-h-[2.25rem]"
              />
            </div>
            {form.operation_status === 'buy' ? (
              <div className="w-1/2 min-w-0 shrink-0">
                <label className="block text-xs text-gray-500 mb-0.5">Redeem date (opcional)</label>
                <DatePicker
                  name="redeem_date"
                  value={form.redeem_date}
                  onChange={handleInputChange}
                  mode="single"
                  colorScheme="green"
                  disabled={submitting}
                  placeholder="Select date"
                  className="w-full justify-between min-h-[2.25rem]"
                />
              </div>
            ) : (
              <div className="w-1/2 min-w-0 shrink-0" aria-hidden />
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Quantidade (opcional)</label>
            <input
              type="text"
              name="quantity"
              value={form.quantity}
              onChange={handleInputChange}
              placeholder="Ex: 100"
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={submitting}
              inputMode="decimal"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor total (R$)</label>
              <input
                type="text"
                name="total_value"
                value={form.total_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
                required
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor líquido (R$)</label>
              <input
                type="text"
                name="net_value"
                value={form.net_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Descrição da taxa (opcional)</label>
            <input
              type="text"
              name="rate_description"
              value={form.rate_description}
              onChange={handleInputChange}
              placeholder="Ex: taxa corretora"
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Observações (opcional)</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleInputChange}
              placeholder="Notas"
              rows={2}
              className="w-full px-2 py-1.5 border rounded-md text-sm resize-none"
              disabled={submitting}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {submitting ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalOperationAdd;
